import React, { useState, useEffect, useRef } from 'react';
import CandorMigrationShowcase from '../components/CandorMigrationShowcase';
import CandorEnterpriseProofSection from '../components/CandorEnterpriseProofSection';

export default function HomePage({ onOpenDownload, onOpenDemo }) {
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [isDemoFullSize, setIsDemoFullSize] = useState(false);
  const demoVideoRef = useRef(null);

  const handleDemoClick = onOpenDemo || (() => setShowDemoModal(true));

  const toggleFullScreenVideo = () => {
    setIsDemoFullSize(prev => !prev);
  };

  // Close modal on Escape key press (ADA WCAG 2.1 - 2.1.1 Keyboard Accessibility)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showDemoModal) {
        setShowDemoModal(false);
        setIsDemoFullSize(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showDemoModal]);

  // Trigger video play when modal opens
  useEffect(() => {
    if (showDemoModal && demoVideoRef.current) {
      demoVideoRef.current.currentTime = 0;
      const playPromise = demoVideoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.log('Video autoplay deferred by browser policy:', error);
        });
      }
    }
  }, [showDemoModal]);

  return (
    <div className="animate-fade-in bg-white min-h-screen">
      {/* SKIP TO MAIN CONTENT LINK FOR SCREEN READERS & KEYBOARD NAV (ADA WCAG 2.4.1) */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:font-extrabold focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Main Content Area */}
      <div id="main-content">
        <CandorMigrationShowcase 
          onOpenDownload={onOpenDownload}
          onOpenDemo={handleDemoClick}
          onOpenSandbox={onOpenDownload}
        />
        <CandorEnterpriseProofSection />
      </div>

      {/* Watch Demo YouTube-Style Video Modal (ADA Accessible Dialog - WCAG 2.1 4.1.2) */}
      {showDemoModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-sm animate-fade-in"
          onClick={() => {
            setShowDemoModal(false);
            setIsDemoFullSize(false);
          }}
          role="presentation"
        >
          <div 
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-modal-title"
            className={`relative bg-[#0f0f0f] border border-neutral-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 ${
              isDemoFullSize 
                ? 'w-full max-w-[95vw] max-h-[92vh] h-[90vh]' 
                : 'w-full max-w-3xl'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* YouTube Player Top Header */}
            <div className="bg-[#181818] px-4 py-2.5 border-b border-neutral-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5 min-w-0">
                {/* YouTube Red Icon Badge */}
                <div className="flex items-center gap-1 bg-red-600 text-white font-black text-[11px] px-2 py-0.5 rounded tracking-tighter shrink-0" aria-hidden="true">
                  <span>▶</span>
                  <span>YouTube</span>
                </div>
                <h2 id="demo-modal-title" className="font-sans text-xs sm:text-sm font-semibold text-neutral-200 truncate">
                  Candor CDP Platform — 1-Shot Schema Awareness & Pipeline Demo
                </h2>
              </div>

              <div className="flex items-center gap-1.5 ml-2 shrink-0">
                {/* Full Size / Compact Toggle Button */}
                <button
                  onClick={toggleFullScreenVideo}
                  aria-label={isDemoFullSize ? "Switch video to compact size" : "Switch video to full screen size"}
                  className="px-2.5 py-1 rounded-md bg-neutral-800 hover:bg-neutral-700 focus-visible:ring-2 focus-visible:ring-blue-500 text-neutral-300 hover:text-white flex items-center gap-1 transition-all text-xs font-medium border border-neutral-700 shadow-sm"
                  title={isDemoFullSize ? "Compact Size" : "Full Size Video"}
                >
                  <span className="text-sm" aria-hidden="true">{isDemoFullSize ? '⤡' : '⛶'}</span>
                  <span className="hidden sm:inline text-[11px] font-semibold">{isDemoFullSize ? 'Compact' : 'Full Size'}</span>
                </button>

                {/* Close Button */}
                <button
                  onClick={() => {
                    setShowDemoModal(false);
                    setIsDemoFullSize(false);
                  }}
                  className="w-7 h-7 rounded-full bg-neutral-800 hover:bg-neutral-700 focus-visible:ring-2 focus-visible:ring-red-500 text-neutral-400 hover:text-white flex items-center justify-center transition-colors text-sm font-bold"
                  aria-label="Close Demo Video Modal"
                >
                  <span aria-hidden="true">✕</span>
                </button>
              </div>
            </div>

            {/* Video Canvas Container (YouTube Dark Player) */}
            <div className={`relative bg-black overflow-hidden group ${isDemoFullSize ? 'flex-1 h-full' : 'aspect-video'}`}>
              <video
                ref={demoVideoRef}
                autoPlay
                controls
                playsInline
                preload="auto"
                className="w-full h-full object-contain"
                aria-label="Candor IDE 1-Shot Demo Video Guide"
              >
                <source src="/assets/Candor_xDE_User_Guide.webm" type="video/webm" />
                <source src="https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4" type="video/mp4" />
                Your browser does not support HTML5 video playback.
              </video>
            </div>

            {/* YouTube Channel Info Bar & Action Bar */}
            <div className="bg-[#181818] px-4 py-3 border-t border-neutral-800 flex flex-wrap items-center justify-between gap-3 text-xs">
              {/* Channel Profile */}
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white font-extrabold text-xs shadow-md" aria-hidden="true">
                  C
                </div>
                <div>
                  <div className="font-bold text-white flex items-center gap-1 text-xs">
                    <span>Candor SI</span>
                    <span className="text-blue-400 text-[10px]" aria-hidden="true">✓</span>
                  </div>
                  <div className="text-[10px] text-neutral-400">12.8k subscribers</div>
                </div>
                <button
                  onClick={() => {
                    setShowDemoModal(false);
                    onOpenDownload();
                  }}
                  aria-label="Try Candor Sandbox in browser"
                  className="ml-2 px-3.5 py-1.5 rounded-full bg-red-600 hover:bg-red-500 focus-visible:ring-2 focus-visible:ring-red-400 text-white font-bold text-xs transition-all shadow-sm flex items-center gap-1"
                >
                  <span aria-hidden="true">⚡</span> Try Sandbox
                </button>
              </div>

              {/* Action CTAs */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowDemoModal(false);
                    onOpenDownload();
                  }}
                  aria-label="Download Desktop App"
                  className="px-3.5 py-1.5 rounded-full bg-neutral-800 hover:bg-neutral-700 focus-visible:ring-2 focus-visible:ring-blue-400 text-neutral-200 font-semibold text-xs transition-colors flex items-center gap-1.5 border border-neutral-700"
                >
                  <span aria-hidden="true">🖥️</span> Get Desktop App
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
